package com.ijoin.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.ijoin.data.api.ContactApi
import com.ijoin.ui.payment.PaymentFlowArgs
import kotlinx.coroutines.launch

/**
 * Wrapper that shows either LandingScreen or EventCardScreen based on whether
 * the event has custom landing HTML content.
 */
@Composable
fun EventScreenWrapper(
    eventId: String,
    isAuthenticated: Boolean,
    onRequireAuth: () -> Unit,
    onBackClick: () -> Unit,
    onBuyTicketClick: (PaymentFlowArgs) -> Unit,
    onContactOrganizerClick: (String, String?) -> Unit,
    onOpenOrganizerProfile: (String) -> Unit,
    onStartChatWithUser: (String, String?) -> Unit,
    onNavigateToTicket: (String) -> Unit = {},
    onEditEvent: () -> Unit = {},
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    selectedTab: String = "home",
    modifier: Modifier = Modifier
) {
    val viewModel = remember(eventId) { EventCardViewModel(eventId) }
    val uiState by viewModel.uiState.collectAsState()
    val scope = rememberCoroutineScope()
    val contactApi = remember { ContactApi() }

    // Track if user clicked "Book" from landing to go to event card
    var showEventCard by remember { mutableStateOf(false) }

    // Handle form submission from landing page
    val handleFormSubmit: (Map<String, Any>) -> Unit = { formData ->
        scope.launch {
            contactApi.submitContact(
                eventId = eventId,
                formData = formData
            ).onSuccess {
                println("Form submitted successfully: $it")
            }.onFailure {
                println("Form submission failed: ${it.message}")
            }
        }
        // Navigate to booking screen after form submission
        showEventCard = true
    }

    when {
        uiState.isLoading -> {
            Box(modifier = modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        }
        uiState.event != null -> {
            val event = uiState.event!!
            val hasLanding = event.landingHtml?.isNotBlank() == true

            if (hasLanding && !showEventCard) {
                // Show landing screen first
                LandingScreen(
                    event = event,
                    onBackClick = onBackClick,
                    onBookClick = { showEventCard = true },
                    onFormSubmit = handleFormSubmit,
                    modifier = modifier
                )
            } else {
                // Show regular event card screen
                EventCardScreen(
                    eventId = eventId,
                    isAuthenticated = isAuthenticated,
                    onRequireAuth = onRequireAuth,
                    onBackClick = {
                        if (hasLanding && showEventCard) {
                            // Go back to landing
                            showEventCard = false
                        } else {
                            onBackClick()
                        }
                    },
                    onBuyTicketClick = onBuyTicketClick,
                    onContactOrganizerClick = onContactOrganizerClick,
                    onOpenOrganizerProfile = onOpenOrganizerProfile,
                    onStartChatWithUser = onStartChatWithUser,
                    onNavigateToTicket = onNavigateToTicket,
                    onEditEvent = onEditEvent,
                    onOpenHome = onOpenHome,
                    onOpenExplore = onOpenExplore,
                    onCreateEventClick = onCreateEventClick,
                    onOpenTickets = onOpenTickets,
                    onOpenProfile = onOpenProfile,
                    selectedTab = selectedTab,
                    modifier = modifier,
                    viewModel = viewModel
                )
            }
        }
        uiState.error != null -> {
            // Show error through EventCardScreen which handles it
            EventCardScreen(
                eventId = eventId,
                isAuthenticated = isAuthenticated,
                onRequireAuth = onRequireAuth,
                onBackClick = onBackClick,
                onBuyTicketClick = onBuyTicketClick,
                onContactOrganizerClick = onContactOrganizerClick,
                onOpenOrganizerProfile = onOpenOrganizerProfile,
                onStartChatWithUser = onStartChatWithUser,
                onNavigateToTicket = onNavigateToTicket,
                onEditEvent = onEditEvent,
                onOpenHome = onOpenHome,
                onOpenExplore = onOpenExplore,
                onCreateEventClick = onCreateEventClick,
                onOpenTickets = onOpenTickets,
                onOpenProfile = onOpenProfile,
                selectedTab = selectedTab,
                modifier = modifier,
                viewModel = viewModel
            )
        }
    }
}
